// Keeps the map light with hundreds of pumps: drops pumps well outside the
// visible region and, when zoomed out with many pumps in view, shows one pump
// per grid cell (the cheapest) so icons that would sit on top of each other
// don't all render. Cells are aligned to fixed lat/lng lines and sized in
// powers of two, so small pans don't reshuffle which pumps are shown.

// Roughly how many cells fit across the visible width; about one zoomed-out
// pin's width per cell on a phone.
const CELLS_ACROSS = 14;
// Pumps this far beyond each edge (as a share of the region) still render,
// so they're already there when the user pans a little.
const OFFSCREEN_MARGIN = 0.5;
// Up to this many pumps in view are all drawn, without thinning.
const MAX_UNTHINNED = 60;

const inRegion = (location, region) => {
  const latPad = region.latitudeDelta * (0.5 + OFFSCREEN_MARGIN);
  const lngPad = region.longitudeDelta * (0.5 + OFFSCREEN_MARGIN);
  return (
    Math.abs(location.lat - region.latitude) <= latPad &&
    Math.abs(location.lng - region.longitude) <= lngPad
  );
};

const priceOf = station => {
  const price = parseFloat(station.latestPrice);
  return Number.isFinite(price) ? price : Infinity;
};

/**
 * @param stations   pumps with `location: {lat, lng}`
 * @param region     current map region ({latitude, longitude, *Delta})
 * @param options.thin     false to only cull (zoomed in)
 * @param options.keepKeys keys always shown (selected, cheapest…)
 * @param options.getKey   station → key
 * @returns pumps to draw as markers
 */
export const visibleStations = (
  stations,
  region,
  {thin = true, keepKeys = [], getKey},
) => {
  if (!region) {
    return stations;
  }

  const kept = [];
  const inView = [];
  stations.forEach(station => {
    const location = station?.location;
    if (typeof location?.lat !== 'number' || typeof location?.lng !== 'number') {
      return;
    }
    if (keepKeys.includes(getKey(station))) {
      kept.push(station);
    } else if (inRegion(location, region)) {
      inView.push(station);
    }
  });

  if (!thin || inView.length <= MAX_UNTHINNED) {
    return kept.concat(inView);
  }

  const cell = Math.pow(
    2,
    Math.round(Math.log2(region.longitudeDelta / CELLS_ACROSS)),
  );
  const cells = new Map();
  inView.forEach(station => {
    const id = `${Math.floor(station.location.lat / cell)}:${Math.floor(
      station.location.lng / cell,
    )}`;
    const current = cells.get(id);
    if (!current || priceOf(station) < priceOf(current)) {
      cells.set(id, station);
    }
  });
  return kept.concat(Array.from(cells.values()));
};
